/**
 * PlanRecommenderWizard — Cuestionario premium en wizard de 5 pasos
 * que recomienda el plan ideal basado en respuestas del lead.
 *
 * Endpoints:
 *   POST /api/public/plan-recommender → { recommended, alternatives }
 *   POST /api/public/sales-contact    → para planes SAS
 *
 * Diseño: dark mode premium con Reveal, micro-animaciones y barra de
 * progreso animada. Auto-escalable: cuando admin agrega planes, este
 * componente automáticamente los considera.
 */
import { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, ArrowRight, Check, Sparkles, X,
  Building2, Car, GraduationCap, ShoppingBag, Wand2,
  Users, TrendingUp, MessageSquare, Mail, Phone, Globe, Target,
} from 'lucide-react';

const INDUSTRIES = [
  { value: 'automotive', label: 'Automotriz', icon: Car, color: 'rose' },
  { value: 'real_estate', label: 'Inmobiliario', icon: Building2, color: 'cyan' },
  { value: 'education', label: 'Educación', icon: GraduationCap, color: 'amber' },
  { value: 'retail', label: 'Retail', icon: ShoppingBag, color: 'sky' },
  { value: 'generic', label: 'Otra industria', icon: Sparkles, color: 'violet' },
];

const TEAM_SIZES = [
  { value: '1', label: 'Solo yo', sub: '1 persona' },
  { value: '2_5', label: 'Equipo chico', sub: '2-5 personas' },
  { value: '6_15', label: 'Equipo mediano', sub: '6-15 personas' },
  { value: '16_50', label: 'Equipo grande', sub: '16-50 personas' },
  { value: 'gt_50', label: 'Corporativo', sub: '50+ personas' },
];

const LEADS_VOLUMES = [
  { value: 'lt_100', label: 'Menos de 100', sub: 'Operación enfocada' },
  { value: '100_500', label: '100 a 500', sub: 'Volumen medio' },
  { value: '500_2k', label: '500 a 2,000', sub: 'Operación intensa' },
  { value: 'gt_2k', label: 'Más de 2,000', sub: 'Volumen alto / enterprise' },
];

const CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { value: 'email', label: 'Email marketing', icon: Mail },
  { value: 'meta', label: 'Facebook / Instagram', icon: Globe },
  { value: 'web', label: 'Chatbot Web', icon: Sparkles },
  { value: 'calls', label: 'Llamadas IA', icon: Phone },
];

const PRIORITIES = [
  { value: 'captar', label: 'Captar más leads', sub: 'Crecer la base', icon: Users },
  { value: 'automatizar', label: 'Automatizar', sub: 'Operar sin gente extra', icon: Wand2 },
  { value: 'cotizar', label: 'Cotizar mejor', sub: 'Vender más rápido', icon: Target },
  { value: 'reportar', label: 'Medir y reportar', sub: 'Tomar mejores decisiones', icon: TrendingUp },
];

const STEPS = ['industry', 'team_size', 'leads_volume', 'channels', 'priority'];

const STEP_HEADLINES = {
  industry: '¿En qué industria operas?',
  team_size: '¿De qué tamaño es tu equipo?',
  leads_volume: '¿Cuántos leads esperas captar al mes?',
  channels: '¿Qué canales necesitas conectar?',
  priority: '¿Cuál es tu prioridad ahora?',
};

const STEP_HINTS = {
  industry: 'Esto define el flujo y los módulos que activamos por ti.',
  team_size: 'Calculamos cuántas licencias de usuario necesitas.',
  leads_volume: 'Te asignamos un plan con el cupo correcto.',
  channels: 'Selecciona todos los que apliquen. Más canales = más cobertura.',
  priority: 'Algunos planes incluyen módulos extra según lo que más te importe hoy.',
};

export default function PlanRecommenderWizard({ open, onClose, onPickPlan, onContactSales }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({
    industry: '',
    team_size: '',
    leads_volume: '',
    channels: [],
    priority: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const currentStep = STEPS[stepIdx];
  const progress = ((stepIdx + 1) / STEPS.length) * 100;
  const canAdvance = useMemo(() => {
    if (currentStep === 'channels') return answers.channels.length > 0;
    return !!answers[currentStep];
  }, [currentStep, answers]);

  const reset = () => {
    setStepIdx(0);
    setAnswers({ industry: '', team_size: '', leads_volume: '', channels: [], priority: '' });
    setResult(null);
  };

  const handleClose = () => { onClose(); reset(); };

  const toggleChannel = (v) => {
    setAnswers(prev => ({
      ...prev,
      channels: prev.channels.includes(v) ? prev.channels.filter(c => c !== v) : [...prev.channels, v],
    }));
  };

  const advance = async () => {
    if (stepIdx < STEPS.length - 1) {
      setStepIdx(stepIdx + 1);
      return;
    }
    // Última pregunta → fetch
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${API}/api/public/plan-recommender`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(answers),
      });
      const data = await r.json();
      setResult(data);
    } catch {
      setResult({ error: true });
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    if (currentStep === 'industry') {
      return (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="step-industry">
          {INDUSTRIES.map(ind => {
            const Icon = ind.icon;
            const active = answers.industry === ind.value;
            return (
              <button
                key={ind.value}
                onClick={() => setAnswers({ ...answers, industry: ind.value })}
                className={`group p-5 rounded-2xl border text-left transition-all ${active ? `border-${ind.color}-400/60 bg-${ind.color}-500/10 scale-[1.02]` : 'border-white/10 bg-white/5 hover:border-white/25 hover:bg-white/8'}`}
                data-testid={`industry-${ind.value}`}
              >
                <div className={`w-10 h-10 rounded-xl bg-${ind.color}-500/15 flex items-center justify-center mb-3`}>
                  <Icon className={`w-5 h-5 text-${ind.color}-300`} />
                </div>
                <p className="text-white font-semibold text-sm">{ind.label}</p>
                {active && <Check className={`w-4 h-4 text-${ind.color}-300 mt-2`} />}
              </button>
            );
          })}
        </div>
      );
    }
    if (currentStep === 'team_size') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="step-team_size">
          {TEAM_SIZES.map(t => {
            const active = answers.team_size === t.value;
            return (
              <button
                key={t.value}
                onClick={() => setAnswers({ ...answers, team_size: t.value })}
                className={`p-4 rounded-2xl border text-center transition-all ${active ? 'border-cyan-400/60 bg-cyan-500/10 scale-[1.02]' : 'border-white/10 bg-white/5 hover:border-white/25'}`}
                data-testid={`team-${t.value}`}
              >
                <p className="text-white font-semibold text-sm">{t.label}</p>
                <p className="text-[10px] text-white/55 mt-1">{t.sub}</p>
              </button>
            );
          })}
        </div>
      );
    }
    if (currentStep === 'leads_volume') {
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="step-leads_volume">
          {LEADS_VOLUMES.map(l => {
            const active = answers.leads_volume === l.value;
            return (
              <button
                key={l.value}
                onClick={() => setAnswers({ ...answers, leads_volume: l.value })}
                className={`p-5 rounded-2xl border text-center transition-all ${active ? 'border-emerald-400/60 bg-emerald-500/10 scale-[1.02]' : 'border-white/10 bg-white/5 hover:border-white/25'}`}
                data-testid={`leads-${l.value}`}
              >
                <p className="text-white font-semibold">{l.label}</p>
                <p className="text-[10px] text-white/55 mt-1">{l.sub}</p>
              </button>
            );
          })}
        </div>
      );
    }
    if (currentStep === 'channels') {
      return (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="step-channels">
          {CHANNELS.map(c => {
            const Icon = c.icon;
            const active = answers.channels.includes(c.value);
            return (
              <button
                key={c.value}
                onClick={() => toggleChannel(c.value)}
                className={`p-4 rounded-2xl border text-center transition-all ${active ? 'border-violet-400/60 bg-violet-500/10 scale-[1.02]' : 'border-white/10 bg-white/5 hover:border-white/25'}`}
                data-testid={`channel-${c.value}`}
              >
                <Icon className={`w-5 h-5 mx-auto mb-2 ${active ? 'text-violet-300' : 'text-white/55'}`} />
                <p className="text-white text-xs">{c.label}</p>
                {active && <Check className="w-3.5 h-3.5 text-violet-300 mx-auto mt-1" />}
              </button>
            );
          })}
        </div>
      );
    }
    if (currentStep === 'priority') {
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="step-priority">
          {PRIORITIES.map(p => {
            const Icon = p.icon;
            const active = answers.priority === p.value;
            return (
              <button
                key={p.value}
                onClick={() => setAnswers({ ...answers, priority: p.value })}
                className={`p-5 rounded-2xl border text-center transition-all ${active ? 'border-amber-400/60 bg-amber-500/10 scale-[1.02]' : 'border-white/10 bg-white/5 hover:border-white/25'}`}
                data-testid={`priority-${p.value}`}
              >
                <Icon className={`w-6 h-6 mx-auto mb-2 ${active ? 'text-amber-300' : 'text-white/55'}`} />
                <p className="text-white font-semibold text-sm">{p.label}</p>
                <p className="text-[10px] text-white/55 mt-1">{p.sub}</p>
              </button>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const renderResult = () => {
    if (!result || result.error) {
      return <p className="text-center text-rose-300 py-8" data-testid="recommender-error">Hubo un problema. Intenta de nuevo o contacta a ventas.</p>;
    }
    const rec = result.recommended;
    if (!rec || !rec.plan) {
      return (
        <div className="text-center py-8 space-y-4" data-testid="recommender-no-match">
          <p className="text-white">No tenemos un plan publicado que encaje aún.</p>
          <Button onClick={() => onContactSales && onContactSales({ source: 'recommender_no_match', industry: answers.industry })} className="bg-cyan-400 text-slate-950">Solicitar cotización personalizada</Button>
        </div>
      );
    }
    const plan = rec.plan;
    const isSas = !!plan.is_sas_only;
    return (
      <div className="space-y-5" data-testid="recommender-result">
        <div className="text-center space-y-2">
          <Sparkles className="w-8 h-8 text-amber-300 mx-auto" />
          <h3 className="text-2xl font-semibold text-white">Tu plan ideal: <span className="text-cyan-300">{plan.name}</span></h3>
          <p className="text-sm text-white/70 max-w-xl mx-auto">{plan.description}</p>
        </div>
        <div className="rounded-2xl border border-cyan-400/30 bg-cyan-400/5 p-5" data-testid="recommender-recommended">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              {isSas ? (
                <p className="text-cyan-200 text-xl font-bold">Plan empresarial · Cotización personalizada</p>
              ) : (
                <p className="text-white text-3xl font-bold">${Number(plan.price || 0).toLocaleString()} <span className="text-sm text-white/60">/{plan.billing_period === 'month' ? 'mes' : plan.billing_period}</span></p>
              )}
            </div>
            <Button
              onClick={() => isSas ? onContactSales && onContactSales({ source: 'recommender_sas', plan_id: plan.id, industry: answers.industry }) : (onPickPlan && onPickPlan(plan))}
              className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold"
              data-testid="recommender-cta"
            >
              {isSas ? 'Contactar a ventas' : 'Empezar prueba'} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          {rec.reasons?.length > 0 && (
            <ul className="mt-4 space-y-1.5">
              {rec.reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-white/75">
                  <Check className="w-3.5 h-3.5 text-emerald-300 mt-0.5 shrink-0" /> {r}
                </li>
              ))}
            </ul>
          )}
        </div>
        {result.alternatives?.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wider text-white/55">Otras alternativas</p>
            <div className="grid md:grid-cols-2 gap-3">
              {result.alternatives.map((alt, idx) => (
                <button
                  key={alt.plan.id}
                  onClick={() => alt.plan.is_sas_only ? onContactSales && onContactSales({ plan_id: alt.plan.id }) : (onPickPlan && onPickPlan(alt.plan))}
                  className="text-left p-4 rounded-xl border border-white/10 bg-white/5 hover:border-white/20 transition"
                  data-testid={`recommender-alternative-${idx}`}
                >
                  <p className="text-white font-semibold text-sm">{alt.plan.name}</p>
                  <p className="text-[10px] text-white/60 mt-1 line-clamp-2">{alt.plan.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}
        <Button onClick={reset} variant="outline" className="w-full border-white/15 text-white/70 hover:text-white">Hacer otra simulación</Button>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-3xl bg-slate-950 border-white/10 text-white p-0 overflow-hidden" data-testid="plan-recommender-dialog">
        <DialogTitle className="sr-only">Simulador de plan ideal</DialogTitle>
        {/* Header */}
        <div className="relative px-7 pt-7 pb-5 border-b border-white/5">
          <button onClick={handleClose} className="absolute top-4 right-4 text-white/40 hover:text-white" data-testid="recommender-close"><X className="w-5 h-5" /></button>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-amber-300" />
            <p className="text-xs uppercase tracking-[0.2em] text-white/60">Simulador inteligente · 1 minuto</p>
          </div>
          {!result && (
            <>
              <h2 className="text-2xl md:text-3xl font-semibold leading-tight" data-testid="recommender-headline">{STEP_HEADLINES[currentStep]}</h2>
              <p className="text-sm text-white/60 mt-1">{STEP_HINTS[currentStep]}</p>
              <div className="mt-5 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-400 to-violet-500 transition-all duration-500" style={{ width: `${progress}%` }} data-testid="recommender-progress" />
              </div>
              <p className="text-[10px] text-white/40 mt-1">Paso {stepIdx + 1} de {STEPS.length}</p>
            </>
          )}
          {result && (
            <h2 className="text-2xl md:text-3xl font-semibold">Resultado de tu simulación</h2>
          )}
        </div>

        {/* Body */}
        <div className="px-7 py-8 min-h-[280px]">
          {loading && (
            <div className="text-center py-16" data-testid="recommender-loading">
              <Sparkles className="w-8 h-8 text-cyan-300 mx-auto animate-pulse" />
              <p className="text-white/70 mt-3">Analizando tus respuestas…</p>
            </div>
          )}
          {!loading && !result && renderStep()}
          {!loading && result && renderResult()}
        </div>

        {/* Footer */}
        {!result && !loading && (
          <div className="flex items-center justify-between px-7 py-4 border-t border-white/5 bg-white/[0.02]">
            <Button
              onClick={() => stepIdx > 0 ? setStepIdx(stepIdx - 1) : handleClose()}
              variant="outline"
              className="border-white/15 text-white/60 hover:text-white"
              data-testid="recommender-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> {stepIdx > 0 ? 'Atrás' : 'Cancelar'}
            </Button>
            <Button
              onClick={advance}
              disabled={!canAdvance}
              className="bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold disabled:opacity-30"
              data-testid="recommender-next"
            >
              {stepIdx === STEPS.length - 1 ? 'Ver mi plan ideal' : 'Siguiente'} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
